import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { useAbility } from "@casl/react";
import { useAuth } from "../../context/AuthContext";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Megaphone,
  Link2,
  Zap,
  LayoutDashboard,
  ChevronRight,
  Loader2,
  ArrowUpDown,
  Trash2,
  Edit,
  AlertTriangle,
  ChevronLeft,
  X,
  GripVertical,
  Settings,
  Plus,
  Save,
  ClipboardList,
  Image,
  CalendarDays,
  Lock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DashboardConfig, Announcement, QuickLink, QuickAction } from "../../types";
import { toSydneyDate } from "../../utils/helpers";
import { api } from "../../utils/api";

/**
 * Truncate HTML content to a character limit while preserving HTML tags.
 * Returns { html: truncated HTML, isTruncated: boolean }.
 */
function truncateHtml(html: string, maxLength: number): { html: string; isTruncated: boolean } {
  if (!html || maxLength <= 0) return { html: html || '', isTruncated: false };

  // Strip tags to count text length
  const textOnly = html.replace(/<[^>]*>/g, '');
  if (textOnly.length <= maxLength) {
    return { html, isTruncated: false };
  }

  // Walk through HTML, counting only text characters
  let result = '';
  let textChars = 0;
  let tagStack: string[] = [];
  let i = 0;

  while (i < html.length && textChars < maxLength) {
    if (html[i] === '<') {
      // Copy the entire tag
      const tagEnd = html.indexOf('>', i);
      if (tagEnd === -1) {
        result += html.slice(i);
        break;
      }
      const tagContent = html.slice(i + 1, tagEnd);
      const tagName = tagContent.split(/\s/)[0].toLowerCase();

      if (tagContent.startsWith('/')) {
        // Closing tag — pop from stack
        const closingName = tagContent.slice(1).split(/\s/)[0].toLowerCase();
        const stackIndex = tagStack.lastIndexOf(closingName);
        if (stackIndex !== -1) {
          tagStack.splice(stackIndex, 1);
        }
      } else if (!tagContent.endsWith('/') && !tagName.match(/^(br|hr|img|input|meta|link|area|base|col|embed|param|source|track|wbr)$/i)) {
        // Opening tag (self-closing tags don't push to stack)
        tagStack.push(tagName);
      }

      result += html.slice(i, tagEnd + 1);
      i = tagEnd + 1;
    } else {
      // Copy text up to next tag or limit
      const nextTag = html.indexOf('<', i);
      const textEnd = nextTag === -1 ? html.length : nextTag;
      const textChunk = html.slice(i, textEnd);
      const remaining = maxLength - textChars;

      if (textChunk.length <= remaining) {
        result += textChunk;
        textChars += textChunk.length;
      } else {
        result += textChunk.slice(0, remaining);
        textChars = maxLength;
      }
      i = textEnd;
    }
  }

  // Close any unclosed tags
  for (let j = tagStack.length - 1; j >= 0; j--) {
    result += `</${tagStack[j]}>`;
  }

  return { html: result + '...', isTruncated: true };
}

// Leave balance type
interface LeaveBalance {
  quota: number;
  adjustment: number;
  used: number;
  remaining: number;
}

interface LeaveBalances {
  "Annual Leave"?: LeaveBalance;
  "Personal Leave"?: LeaveBalance;
}

// Leave Balances Card Component
function LeaveBalancesCard() {
  const [balances, setBalances] = useState<LeaveBalances | null>(null);
  const [upcomingLeave, setUpcomingLeave] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const [balanceResponse, leavesResponse] = await Promise.all([
          api.get("/api/attendance/leave-balance"),
          api.get("/api/attendance/my-leave-requests?status=Approved")
        ]);

        if (balanceResponse?.success) {
          setBalances(balanceResponse.data);
        }

        if (leavesResponse?.success && leavesResponse.data?.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const upcoming = leavesResponse.data
            .filter((leave: any) => new Date(leave.startDate) >= today)
            .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0];
          setUpcomingLeave(upcoming || null);
        }
      } catch (error) {
        console.error("Failed to fetch leave balances:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, []);

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
    const startStr = start.toLocaleDateString("en-GB", options);
    const endStr = end.toLocaleDateString("en-GB", options);
    const startDay = start.getDay();
    const endDay = end.getDay();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return `${dayNames[startDay]} ${startStr} - ${dayNames[endDay]} ${endStr}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Leave Balances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasAnyBalance = balances && (balances["Annual Leave"] || balances["Personal Leave"]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <CalendarDays className="h-5 w-5" />
          Leave Balances
        </CardTitle>
        <button
          onClick={() => navigate("/attendance/request-leave")}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
        >
          Request leave
        </button>
      </CardHeader>
      <CardContent className="pt-0 space-y-5">
        {/* Annual & Personal Leave */}
        {hasAnyBalance ? (
          <div className="grid grid-cols-2 gap-0 rounded-xl border border-gray-200 overflow-hidden">
            {/* Annual Leave */}
            <div className="p-5 flex flex-col items-center gap-3 border-r border-gray-200">
              <p className="text-sm font-semibold text-gray-900">Annual Leave</p>
              <div className="w-12 h-0.5 bg-gray-200" />
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
                {balances?.["Annual Leave"]?.remaining?.toFixed(4) ?? "0.0000"} hours
              </span>
            </div>
            {/* Personal Leave */}
            <div className="p-5 flex flex-col items-center gap-3">
              <p className="text-sm font-semibold text-gray-900">Personal Leave</p>
              <div className="w-12 h-0.5 bg-gray-200" />
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                {balances?.["Personal Leave"]?.remaining?.toFixed(4) ?? "0.0000"} hours
              </span>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-4">No leave balance data available</p>
        )}

        {/* Upcoming Leave */}
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Upcoming Leave</h3>
          {upcomingLeave ? (
            <>
              <p className="text-sm text-gray-500 mb-3">Approved upcoming leave.</p>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  {upcomingLeave.type}
                </span>
                <span className="text-sm text-gray-900">
                  {formatDateRange(upcomingLeave.startDate, upcomingLeave.endDate)}
                </span>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No upcoming leave.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Icon type - using React.ComponentType with SVG props
type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Icon mapping for quick actions
const ICON_MAP: Record<string, IconComponent> = {
  Megaphone,
  Link2,
  Zap,
  LayoutDashboard,
  Plus,
  Settings,
  ClipboardList,
};

function getIcon(iconName: string): IconComponent {
  return ICON_MAP[iconName] || Zap;
}

// Fetch full dashboard data
const getDashboardData = async (): Promise<DashboardConfig | null> => {
  try {
    const response = await api.get("/api/dashboard");
    return response.data || null;
  } catch (error) {
    console.error("Failed to fetch dashboard data:", error);
    return null;
  }
};

// Save announcements
const saveAnnouncements = async (announcements: Announcement[]) => {
  return api.put("/api/dashboard/announcements", { announcements });
};

// Save quick links
const saveQuickLinks = async (quickLinks: QuickLink[]) => {
  return api.put("/api/dashboard/quick-links", { quickLinks });
};

// Save quick actions master list
const saveQuickActions = async (quickActions: QuickAction[]) => {
  return api.put("/api/dashboard/actions", { quickActions });
};

// Save user's selected quick actions
const saveMyQuickActions = async (selectedQuickActions: string[]) => {
  return api.put("/api/dashboard/my-actions", { selectedQuickActions });
};

export default function DashboardPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  
  // View state
  const [view, setView] = useState<"dashboard" | "settings">("dashboard");
  
  // Data state
  const [dashboardData, setDashboardData] = useState<DashboardConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Settings state
  const [activeSettingsTab, setActiveSettingsTab] = useState<"news" | "links" | "actions">("news");
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>([]);
  const [masterQuickActions, setMasterQuickActions] = useState<QuickAction[]>([]);
  const [myQuickActionIds, setMyQuickActionIds] = useState<string[]>([]);
  
  // Quick actions picker modal
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<string[]>([]);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<string | null>(null);
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null);
  const [actionToDelete, setActionToDelete] = useState<string | null>(null);
  
  // Saving state for individual items
  const [savingLinkId, setSavingLinkId] = useState<string | null>(null);
  const [savingActionId, setSavingActionId] = useState<string | null>(null);
  
  const [announcementPreviewLength, setAnnouncementPreviewLength] = useState<number>(500);
  const [savingPreviewLength, setSavingPreviewLength] = useState(false);
  const [dashboardPage, setDashboardPage] = useState(1);
  const [settingsPage, setSettingsPage] = useState(1);
  const DASHBOARD_PAGE_SIZE = 5;
  const SETTINGS_PAGE_SIZE = 10;

  // Granular dashboard permissions control who can edit the shared widget
  // configuration. Only the exact permission grants access (no role bypass).
  const canReadSettings = ability.can(ACTIONS.READ, RESOURCES.DASHBOARD_SETTINGS);
  const canUpdateSettings = ability.can(ACTIONS.UPDATE, RESOURCES.DASHBOARD_SETTINGS);
  const canReadAnnouncements = ability.can(ACTIONS.READ, RESOURCES.DASHBOARD_ANNOUNCEMENT);
  const canCreateAnnouncement = ability.can(ACTIONS.CREATE, RESOURCES.DASHBOARD_ANNOUNCEMENT);
  const canUpdateAnnouncement = ability.can(ACTIONS.UPDATE, RESOURCES.DASHBOARD_ANNOUNCEMENT);
  const canDeleteAnnouncement = ability.can(ACTIONS.DELETE, RESOURCES.DASHBOARD_ANNOUNCEMENT);
  const canUpdateQuickActions = ability.can(ACTIONS.UPDATE, RESOURCES.DASHBOARD_QUICK_ACTIONS);
  const canUpdateQuickLinks = ability.can(ACTIONS.UPDATE, RESOURCES.DASHBOARD_QUICK_LINKS);

  // Users with "Show in Attendance and Leave" toggled off must not see any
  // attendance/leave content on the dashboard
  const hasAttendanceAccess = currentUser?.showInAttendance !== false;
  const isAttendanceActionUrl = (url: string) =>
    url === "/attendance" || url.startsWith("/attendance/");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const data = await getDashboardData();
      if (data) {
        setDashboardData(data);
        setAnnouncements(data.announcements || []);
        setQuickLinks(data.quickLinks || []);
        setMasterQuickActions(data.quickActions || []);
        setMyQuickActionIds(data.myQuickActions || []);
        setAnnouncementPreviewLength(data.announcementPreviewLength ?? 500);
      }
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // Quick actions pinned by an admin ("Always show") are always selected,
  // appear first, and cannot be removed by users
  const pinnedQuickActions = masterQuickActions.filter(action => action.alwaysShow);
  const pinnedQuickActionIds = pinnedQuickActions.map(action => action.id);

  // Get user's selected quick actions (pinned ones are prepended below)
  const selectedQuickActions = masterQuickActions.filter(
    action => myQuickActionIds.includes(action.id) && !action.alwaysShow
  ).sort((a, b) => {
    const indexA = myQuickActionIds.indexOf(a.id);
    const indexB = myQuickActionIds.indexOf(b.id);
    return indexA - indexB;
  });

  const myQuickActions = [...pinnedQuickActions, ...selectedQuickActions];

  // By default (no personal selection) every user sees the
  // admin-configured master list of quick actions, pinned ones first
  const defaultQuickActions = [...pinnedQuickActions, ...masterQuickActions.filter(action => !action.alwaysShow)];
  const displayedQuickActions = (myQuickActionIds.length > 0 && myQuickActions.length > 0
    ? myQuickActions
    : defaultQuickActions
  ).filter(action => hasAttendanceAccess || !isAttendanceActionUrl(action.url));

  // Picker selection always starts with the pinned actions, then the user's own
  const getInitialPickerSelection = () => [
    ...pinnedQuickActionIds,
    ...myQuickActionIds.filter(id => !pinnedQuickActionIds.includes(id)),
  ];

  // Open the picker initialized with the user's current selection
  const openPicker = () => {
    setPickerSelection(getInitialPickerSelection());
    setShowPicker(true);
  };

  // Handle quick actions picker save
  const handleSaveMyActions = async () => {
    // Pinned actions are always kept in the saved selection
    const selection = [
      ...pinnedQuickActionIds,
      ...pickerSelection.filter(id => !pinnedQuickActionIds.includes(id)),
    ];
    try {
      await saveMyQuickActions(selection);
      setMyQuickActionIds(selection);
      setShowPicker(false);
    } catch (error) {
      console.error("Failed to save quick actions:", error);
    }
  };

  // Toggle action selection in picker
  const toggleActionSelection = (actionId: string) => {
    // Pinned ("Always show") actions are locked and cannot be removed
    if (pinnedQuickActionIds.includes(actionId)) return;
    setPickerSelection(prev => {
      if (prev.includes(actionId)) {
        return prev.filter(id => id !== actionId);
      }
      if (prev.length >= 6) {
        return prev; // Max 6
      }
      return [...prev, actionId];
    });
  };

  // Move action up/down in picker
  const moveAction = (index: number, direction: "up" | "down") => {
    setPickerSelection(prev => {
      const newOrder = [...prev];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= newOrder.length) return prev;
      [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
      return newOrder;
    });
  };

  // Update announcement
  const updateAnnouncement = (id: string, updates: Partial<Announcement>) => {
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
  };

  // Save announcement preview length
  const saveAnnouncementPreviewLength = async (length: number) => {
    setSavingPreviewLength(true);
    try {
      await api.put("/api/dashboard/announcement-preview-length", { announcementPreviewLength: length });
      toast.success("Preview length saved successfully");
    } catch (error) {
      console.error("Failed to save preview length:", error);
      toast.error("Failed to save preview length");
    } finally {
      setSavingPreviewLength(false);
    }
  };
  const goToAddAnnouncement = () => {
    navigate("/announcements/new");
  };

  // Navigate to edit announcement form
  const goToEditAnnouncement = (id: string) => {
    navigate(`/announcements/${id}/edit`);
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setAnnouncementToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Open delete confirmation for link
  const openDeleteLinkDialog = (id: string) => {
    setLinkToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Open delete confirmation for action
  const openDeleteActionDialog = (id: string) => {
    setActionToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (announcementToDelete) {
      const updated = announcements.filter(a => a.id !== announcementToDelete);
      setAnnouncements(updated);
      try {
        await saveAnnouncements(updated);
      } catch (error) {
        console.error("Failed to save announcements:", error);
      }
      setAnnouncementToDelete(null);
    }
    if (linkToDelete) {
      const updated = quickLinks.filter(l => l.id !== linkToDelete);
      setQuickLinks(updated);
      try {
        await saveQuickLinks(updated);
      } catch (error) {
        console.error("Failed to save quick links:", error);
      }
      setLinkToDelete(null);
    }
    if (actionToDelete) {
      const updated = masterQuickActions.filter(a => a.id !== actionToDelete);
      setMasterQuickActions(updated);
      setMyQuickActionIds(prev => prev.filter(actionId => actionId !== actionToDelete));
      setPickerSelection(prev => prev.filter(actionId => actionId !== actionToDelete));
      try {
        await saveQuickActions(updated);
      } catch (error) {
        console.error("Failed to save quick actions:", error);
      }
      setActionToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  // Add new quick link
  const addQuickLink = () => {
    if (quickLinks.length >= 10) return; // Max 10
    const newLink: QuickLink = {
      id: crypto.randomUUID(),
      title: "",
      url: "",
    };
    setQuickLinks([...quickLinks, newLink]);
  };

  // Save quick link
  const saveQuickLinkItem = async (id: string) => {
    const link = quickLinks.find(l => l.id === id);
    if (!link) return;
    setSavingLinkId(id);
    try {
      await saveQuickLinks(quickLinks);
      toast.success("Quick link saved successfully");
    } catch (error) {
      console.error("Failed to save quick links:", error);
      toast.error("Failed to save quick link");
    } finally {
      setSavingLinkId(null);
    }
  };

  // Update quick link (local only)
  const updateQuickLink = (id: string, updates: Partial<QuickLink>) => {
    setQuickLinks(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  // Remove quick link
  const removeQuickLink = async (id: string) => {
    const updated = quickLinks.filter(l => l.id !== id);
    setQuickLinks(updated);
    try {
      await saveQuickLinks(updated);
    } catch (error) {
      console.error("Failed to save quick links:", error);
    }
  };

  // Add new quick action
  const addQuickAction = () => {
    const newAction: QuickAction = {
      id: crypto.randomUUID(),
      label: "",
      icon: "Zap",
      url: "",
      alwaysShow: false,
    };
    setMasterQuickActions([...masterQuickActions, newAction]);
  };

  // Save quick action
  const saveQuickActionItem = async (id: string) => {
    const action = masterQuickActions.find(a => a.id === id);
    if (!action) return;
    setSavingActionId(id);
    try {
      await saveQuickActions(masterQuickActions);
      toast.success("Quick action saved successfully");
    } catch (error) {
      console.error("Failed to save quick actions:", error);
      toast.error("Failed to save quick action");
    } finally {
      setSavingActionId(null);
    }
  };

  // Update quick action (local only)
  const updateQuickAction = (id: string, updates: Partial<QuickAction>) => {
    setMasterQuickActions(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
  };

  // Remove quick action
  const removeQuickAction = async (id: string) => {
    const updated = masterQuickActions.filter(a => a.id !== id);
    setMasterQuickActions(updated);
    // Also remove from user's selection if present
    setMyQuickActionIds(prev => prev.filter(actionId => actionId !== id));
    setPickerSelection(prev => prev.filter(actionId => actionId !== id));
    try {
      await saveQuickActions(updated);
    } catch (error) {
      console.error("Failed to save quick actions:", error);
    }
  };

  // Available icons for quick actions
  const availableIcons = Object.keys(ICON_MAP);

  // Attendance/leave actions are hidden from the picker for users without
  // module access. Hidden selections are kept (not deleted) so they reappear
  // if the toggle is turned back on.
  const visiblePickerSelection = pickerSelection.filter(id => {
    const action = masterQuickActions.find(a => a.id === id);
    return action && (hasAttendanceAccess || !isAttendanceActionUrl(action.url));
  });

  // Quick actions picker dialog — shared by the dashboard view (all users
  // can configure their own quick actions) and the settings view
  const quickActionsPickerDialog = (
    <Dialog open={showPicker} onOpenChange={setShowPicker}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Select Quick Actions</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select up to 6 quick actions. Drag to reorder. Actions marked "Always show" are set by an admin and cannot be removed.
          </p>

          {/* Selected Actions */}
          {visiblePickerSelection.length > 0 && (
            <div className="space-y-2">
              <Label>Selected ({visiblePickerSelection.length}/6)</Label>
              <div className="space-y-1">
                {pickerSelection.map((actionId, index) => {
                  const action = masterQuickActions.find(a => a.id === actionId);
                  if (!action) return null;
                  if (!hasAttendanceAccess && isAttendanceActionUrl(action.url)) return null;
                  const Icon = getIcon(action.icon);
                  const isPinned = !!action.alwaysShow;
                  return (
                    <div key={actionId} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                      <Icon className="h-4 w-4" />
                      <span className="flex-1 text-sm">{action.label}</span>
                      {isPinned && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                          <Lock className="h-3 w-3" />
                          Always show
                        </span>
                      )}
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveAction(index, "up")}
                          disabled={index === 0 || isPinned}
                        >
                          <ArrowUpDown className="h-3 w-3 rotate-180" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveAction(index, "down")}
                          disabled={index === pickerSelection.length - 1 || isPinned}
                        >
                          <ArrowUpDown className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive"
                          onClick={() => toggleActionSelection(actionId)}
                          disabled={isPinned}
                          title={isPinned ? "Added by admin — always shown" : "Remove"}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available Actions */}
          <div className="space-y-2">
            <Label>Available Actions</Label>
            <ScrollArea className="h-48">
              <div className="space-y-1">
                {masterQuickActions
                  .filter(action => !pickerSelection.includes(action.id))
                  .filter(action => hasAttendanceAccess || !isAttendanceActionUrl(action.url))
                  .map((action) => {
                    const Icon = getIcon(action.icon);
                    return (
                      <button
                        key={action.id}
                        onClick={() => toggleActionSelection(action.id)}
                        disabled={pickerSelection.length >= 6}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-left"
                      >
                        <div className="rounded-full p-1.5 bg-primary/10 text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <span className="text-sm">{action.label}</span>
                        <Plus className="h-4 w-4 ml-auto text-muted-foreground" />
                      </button>
                    );
                  })}
                {masterQuickActions.filter(action => !pickerSelection.includes(action.id)).filter(action => hasAttendanceAccess || !isAttendanceActionUrl(action.url)).length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-4">All actions selected</p>
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setPickerSelection(getInitialPickerSelection());
              setShowPicker(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveMyActions}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ========== SETTINGS VIEW ==========
  if (view === "settings" && canReadSettings) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setView("dashboard")}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Dashboard Settings</h1>
        </div>

        <Tabs value={activeSettingsTab} onValueChange={(v) => setActiveSettingsTab(v as any)}>
          <TabsList className="bg-gray-100 p-1 rounded-full w-fit">
            <TabsTrigger value="news" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
              <Megaphone className="h-4 w-4 mr-2" />
              News & Announcements
            </TabsTrigger>
            <TabsTrigger value="links" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
              <Link2 className="h-4 w-4 mr-2" />
              Quick Links
            </TabsTrigger>
            <TabsTrigger value="actions" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
              <Zap className="h-4 w-4 mr-2" />
              Quick Actions
            </TabsTrigger>
          </TabsList>

          {/* News & Announcements Tab */}
          <TabsContent value="news" className="space-y-4">
            {/* Preview Length Setting */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Announcement Preview</CardTitle>
                <CardDescription>
                  Set how much of the announcement body is shown on the dashboard
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="preview-length" className="text-sm whitespace-nowrap">Character limit</Label>
                    <Input
                      id="preview-length"
                      type="number"
                      min={0}
                      max={5000}
                      value={announcementPreviewLength}
                      onChange={(e) => setAnnouncementPreviewLength(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      className="w-24"
                      disabled={!canUpdateSettings}
                    />
                  </div>
                  <Button
                    onClick={() => saveAnnouncementPreviewLength(announcementPreviewLength)}
                    disabled={savingPreviewLength || !canUpdateSettings}
                    size="sm"
                  >
                    {savingPreviewLength ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Announcements</CardTitle>
                  <CardDescription>Manage news and announcements shown on the dashboard</CardDescription>
                </div>
                {canCreateAnnouncement && (
                  <Button onClick={goToAddAnnouncement}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Announcement
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const sortedAnnouncements = [...announcements]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                  const totalPages = Math.ceil(sortedAnnouncements.length / SETTINGS_PAGE_SIZE) || 1;
                  const startIndex = (settingsPage - 1) * SETTINGS_PAGE_SIZE;
                  const paginatedAnnouncements = sortedAnnouncements.slice(startIndex, startIndex + SETTINGS_PAGE_SIZE);

                  return (
                    <>
                      {paginatedAnnouncements.map((announcement) => (
                        <Card key={announcement.id} className="border">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                              {announcement.imageUrl && (
                                <div className="sm:w-48 md:w-56 flex-shrink-0">
                                  <img
                                    src={announcement.imageUrl}
                                    alt={announcement.title}
                                    className="w-full h-32 sm:h-28 object-cover rounded-lg"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-semibold text-base leading-snug">
                                    {announcement.title}
                                  </h3>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {canUpdateAnnouncement && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => goToEditAnnouncement(announcement.id)}
                                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canDeleteAnnouncement && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openDeleteDialog(announcement.id)}
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {(() => {
                                  const { html, isTruncated } = truncateHtml(announcement.content || '', 200);
                                  return (
                                  <>
                                    <div
                                      className="text-sm text-muted-foreground ql-editor p-0 overflow-visible"
                                      dangerouslySetInnerHTML={{ __html: html }}
                                    />
                                    {isTruncated && (
                                      <span className="text-sm text-muted-foreground">...</span>
                                    )}
                                  </>
                                  );
                                })()}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{toSydneyDate(announcement.createdAt)}</span>
                                  {announcement.createdByName && (
                                    <>
                                      <span>·</span>
                                      <span>By {announcement.createdByName}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {announcements.length === 0 && (
                        <p className="text-muted-foreground text-center py-8">No announcements yet. Click "Add Announcement" to create one.</p>
                      )}

                      {/* Pagination */}
                      {sortedAnnouncements.length > SETTINGS_PAGE_SIZE && (
                        <div className="flex items-center justify-between pt-4 border-t">
                          <p className="text-sm text-muted-foreground">
                            Showing {startIndex + 1}-{Math.min(startIndex + SETTINGS_PAGE_SIZE, sortedAnnouncements.length)} of {sortedAnnouncements.length}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSettingsPage(p => Math.max(1, p - 1))}
                              disabled={settingsPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground">
                              Page {settingsPage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSettingsPage(p => Math.min(totalPages, p + 1))}
                              disabled={settingsPage === totalPages}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Links Tab */}
          <TabsContent value="links" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Quick Links</CardTitle>
                  <CardDescription>Manage quick links shown on the dashboard (max 10)</CardDescription>
                </div>
                <Button onClick={addQuickLink} disabled={quickLinks.length >= 10 || !canUpdateQuickLinks}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Link
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {quickLinks.map((link) => (
                  <div key={link.id} className="flex items-center gap-2 p-3 border rounded-lg">
                    <Input
                      value={link.title}
                      onChange={(e) => updateQuickLink(link.id, { title: e.target.value })}
                      placeholder="Link title"
                      className="flex-1"
                      disabled={!canUpdateQuickLinks}
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateQuickLink(link.id, { url: e.target.value })}
                      placeholder="URL"
                      className="flex-[2]"
                      disabled={!canUpdateQuickLinks}
                    />
                    <Select
                      value={link.icon || "Link2"}
                      onValueChange={(value) => updateQuickLink(link.id, { icon: value })}
                      disabled={!canUpdateQuickLinks}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableIcons.map(iconName => {
                          const Icon = getIcon(iconName);
                          return (
                            <SelectItem key={iconName} value={iconName}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {iconName}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => saveQuickLinkItem(link.id)}
                      disabled={savingLinkId === link.id || !canUpdateQuickLinks}
                      className="text-primary"
                      title="Save link"
                    >
                      {savingLinkId === link.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteLinkDialog(link.id)}
                      className="text-destructive"
                      title="Delete link"
                      disabled={!canUpdateQuickLinks}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {quickLinks.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No quick links yet. Click "Add Link" to create one.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>Manage the master list of quick actions users can choose from</CardDescription>
                </div>
                <Button onClick={addQuickAction} disabled={!canUpdateQuickActions}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Action
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {masterQuickActions.map((action) => (
                  <div key={action.id} className="flex items-center gap-2 p-3 border rounded-lg">
                    <Select
                      value={action.icon || "Zap"}
                      onValueChange={(value) => updateQuickAction(action.id, { icon: value })}
                      disabled={!canUpdateQuickActions}
                    >
                      <SelectTrigger className="w-[60px] px-2">
                        <SelectValue placeholder="Icon">
                          {(() => {
                            const Icon = getIcon(action.icon || "Zap");
                            return <Icon className="h-4 w-4" />;
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableIcons.map(iconName => {
                          const Icon = getIcon(iconName);
                          return (
                            <SelectItem key={iconName} value={iconName}>
                              <Icon className="h-4 w-4" />
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Input
                      value={action.label}
                      onChange={(e) => updateQuickAction(action.id, { label: e.target.value })}
                      placeholder="Action label"
                      className="flex-1"
                      disabled={!canUpdateQuickActions}
                    />
                    <Input
                      value={action.url}
                      onChange={(e) => updateQuickAction(action.id, { url: e.target.value })}
                      placeholder="URL or route"
                      className="flex-[2]"
                      disabled={!canUpdateQuickActions}
                    />
                    <div
                      className="flex items-center gap-1.5 px-1"
                      title="Always show this action on every user's dashboard"
                    >
                      <Checkbox
                        id={`always-show-${action.id}`}
                        checked={!!action.alwaysShow}
                        onCheckedChange={(checked) => updateQuickAction(action.id, { alwaysShow: checked === true })}
                        disabled={!canUpdateQuickActions}
                      />
                      <Label
                        htmlFor={`always-show-${action.id}`}
                        className="text-xs text-muted-foreground whitespace-nowrap cursor-pointer"
                      >
                        Always show
                      </Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => saveQuickActionItem(action.id)}
                      disabled={savingActionId === action.id || !canUpdateQuickActions}
                      className="text-primary"
                      title="Save action"
                    >
                      {savingActionId === action.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteActionDialog(action.id)}
                      className="text-destructive"
                      title="Delete action"
                      disabled={!canUpdateQuickActions}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {masterQuickActions.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No quick actions yet. Click "Add Action" to create one.</p>
                )}
              </CardContent>
            </Card>

            {/* My Quick Actions Selection */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>My Quick Actions</CardTitle>
                  <CardDescription>Select which quick actions appear on your dashboard (max 6)</CardDescription>
                </div>
                <Button variant="outline" onClick={openPicker}>
                  <Settings className="h-4 w-4 mr-2" />
                  Select Actions
                </Button>
              </CardHeader>
              <CardContent>
                {myQuickActions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {myQuickActions.map((action) => {
                      const Icon = getIcon(action.icon);
                      return (
                        <div key={action.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-gray-50">
                          <Icon className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">{action.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No quick actions selected. Click "Select Actions" to choose which actions appear on your dashboard.</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions Picker Dialog */}
            {quickActionsPickerDialog}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete {announcementToDelete ? "Announcement" : linkToDelete ? "Quick Link" : "Quick Action"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this {announcementToDelete ? "announcement" : linkToDelete ? "quick link" : "quick action"}? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setAnnouncementToDelete(null);
                  setLinkToDelete(null);
                  setActionToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== DASHBOARD VIEW ==========
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">
          Hi {currentUser?.name}, Welcome to Idarah
        </h1>
        <div className="flex items-center gap-4">
          {canReadSettings && (
            <Button variant="outline" size="sm" onClick={() => setView("settings")}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
        {/* Left Column - All content (desktop) */}
        {/* Order 1 on mobile: Announcements first */}
        <div className="lg:col-span-3 space-y-6 order-1">
          {/* News & Announcements — only visible with read:dashboard_announcement */}
          {canReadAnnouncements && (
          <Card className="bg-white border border-gray-200">
            <CardContent className="p-0">
              {announcements.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {(() => {
                    const sortedAnnouncements = [...announcements]
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    const totalPages = Math.ceil(sortedAnnouncements.length / DASHBOARD_PAGE_SIZE) || 1;
                    const startIndex = (dashboardPage - 1) * DASHBOARD_PAGE_SIZE;
                    const paginatedAnnouncements = sortedAnnouncements.slice(startIndex, startIndex + DASHBOARD_PAGE_SIZE);

                    return (
                      <>
                        {paginatedAnnouncements.map((announcement) => (
                          <div key={announcement.id} className="p-5">
                            <div className="flex flex-col sm:flex-row gap-5">
                              {/* Image placeholder */}
                              <div className="sm:w-40 md:w-44 flex-shrink-0">
                                <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                                  {announcement.imageUrl ? (
                                    <img
                                      src={announcement.imageUrl}
                                      alt={announcement.title}
                                      className="w-full h-full object-cover rounded-lg"
                                    />
                                  ) : (
                                    <Image className="h-8 w-8 text-gray-400" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0 space-y-2">
                                {/* Badge */}
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                  News & announcements
                                </span>
                                <h3
                                  className="font-semibold text-base leading-snug text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                                  onClick={() => navigate(`/announcements/${announcement.id}`)}
                                >
                                  {announcement.title}
                                </h3>
                                {/* Extract date/subtitle from content if possible, otherwise show date */}
                                <p className="text-sm text-gray-500">
                                  {announcement.content
                                    ? (() => {
                                        const text = announcement.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                                        const dateMatch = text.match(/(\d{1,2}\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);
                                        return dateMatch ? `Join us on ${dateMatch[1]}` : toSydneyDate(announcement.createdAt);
                                      })()
                                    : toSydneyDate(announcement.createdAt)
                                  }
                                </p>
                                {(() => {
                                  const limit = announcementPreviewLength || 500;
                                  const { html, isTruncated } = truncateHtml(announcement.content || '', limit);
                                  return (
                                    <div className="space-y-2">
                                      <div
                                        className="text-sm text-gray-600 ql-editor p-0 overflow-visible"
                                        dangerouslySetInnerHTML={{ __html: html }}
                                      />
                                      {isTruncated && (
                                        <button
                                          onClick={() => navigate(`/announcements/${announcement.id}`)}
                                          className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-0.5 mt-2"
                                        >
                                          Read more...
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Pagination */}
                        {sortedAnnouncements.length > DASHBOARD_PAGE_SIZE && (
                          <div className="flex items-center justify-between p-5 border-t border-gray-100">
                            <p className="text-sm text-muted-foreground">
                              Showing {startIndex + 1}-{Math.min(startIndex + DASHBOARD_PAGE_SIZE, sortedAnnouncements.length)} of {sortedAnnouncements.length}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDashboardPage(p => Math.max(1, p - 1))}
                                disabled={dashboardPage === 1}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              <span className="text-sm text-muted-foreground">
                                Page {dashboardPage} of {totalPages}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDashboardPage(p => Math.min(totalPages, p + 1))}
                                disabled={dashboardPage === totalPages}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No announcements yet</p>
              )}
            </CardContent>
          </Card>
          )}

          {/* Quick Links, Quick Actions & Leave Balances Row */}
          <div className={`grid grid-cols-1 ${hasAttendanceAccess ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
            {/* Quick Links */}
            <Card className="rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base font-semibold">Quick links</CardTitle>
                {canUpdateQuickLinks && quickLinks.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5" onClick={() => { setActiveSettingsTab("links"); setView("settings"); }}>
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {quickLinks.length > 0 ? (
                  <div className="space-y-3">
                    {quickLinks.map((link, index) => {
                      const Icon = getIcon(link.icon || "Link2");
                      // Rotate through a palette of soft icon background colors
                      const iconColors = [
                        "bg-violet-100 text-violet-600",
                        "bg-emerald-100 text-emerald-600",
                        "bg-amber-100 text-amber-600",
                        "bg-sky-100 text-sky-600",
                        "bg-rose-100 text-rose-600",
                        "bg-indigo-100 text-indigo-600",
                        "bg-teal-100 text-teal-600",
                        "bg-orange-100 text-orange-600",
                      ];
                      const colorClass = iconColors[index % iconColors.length];
                      return (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 px-4 py-3.5 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all group"
                        >
                          <div className={`rounded-lg p-2 ${colorClass}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className="text-sm font-medium text-gray-900 flex-1">{link.title}</span>
                          <svg
                            className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M7 17L17 7" />
                            <path d="M7 7h10v10" />
                          </svg>
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground text-sm mb-2">No quick links configured</p>
                    {canUpdateQuickLinks && (
                      <Button variant="outline" size="sm" onClick={() => { setActiveSettingsTab("links"); setView("settings"); }}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Link
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base font-semibold">Quick actions</CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={openPicker}
                    title="Select quick actions"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  {canUpdateQuickActions && (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5" onClick={() => { setActiveSettingsTab("actions"); setView("settings"); }}>
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {displayedQuickActions.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {displayedQuickActions.map((action, index) => {
                      const Icon = getIcon(action.icon);
                      const isExternal = action.url.startsWith("http");
                      const iconColors = [
                        "bg-blue-100 text-blue-600",
                        "bg-violet-100 text-violet-600",
                        "bg-emerald-100 text-emerald-600",
                        "bg-amber-100 text-amber-600",
                        "bg-sky-100 text-sky-600",
                        "bg-rose-100 text-rose-600",
                      ];
                      const colorClass = iconColors[index % iconColors.length];

                      const cardContent = (
                        <>
                          <div className={`rounded-xl p-2.5 ${colorClass}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="text-xs font-medium text-gray-700">{action.label}</span>
                        </>
                      );

                      return isExternal ? (
                        <a
                          key={action.id}
                          href={action.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center gap-2.5 p-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all text-center"
                        >
                          {cardContent}
                        </a>
                      ) : (
                        <Link
                          key={action.id}
                          to={action.url}
                          className="flex flex-col items-center gap-2.5 p-4 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-gray-300 transition-all text-center"
                        >
                          {cardContent}
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground text-sm mb-2">No quick actions configured</p>
                    {canUpdateQuickActions && (
                      <Button variant="outline" size="sm" onClick={() => { setActiveSettingsTab("actions"); setView("settings"); }}>
                        <Settings className="h-4 w-4 mr-1" />
                        Configure in Settings
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leave Balances — hidden for users without attendance module access */}
            {hasAttendanceAccess && <LeaveBalancesCard />}
          </div>

          {/* Work Queue */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Work Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <p className="text-muted-foreground">Coming soon</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions Picker Dialog — available to all users with dashboard access */}
      {quickActionsPickerDialog}
    </div>
  );
}
